import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../components/PageHeader'
import { Button, Card, EmptyState, Input, Modal, ProviderTag, Skeleton, Spinner } from '../components/ui'
import { useAvailableSites, useEdgeGateways } from '../api/hooks'
import { api, ApiError } from '../api/client'
import type { AccessSystem, SystemProvider } from '../api/types'
import { useAuthStore } from '../stores/auth'
import {
  DEFAULT_POLL_INTERVAL,
  FLEXC_RECEIVER_HOST,
  FLEXC_RECEIVER_PORT,
  generateFlexcKey,
  PANEL_CONSTANTS,
} from '../components/features/FlexCProvisioningCard'

interface ProviderInfo {
  id: SystemProvider
  name: string
  blurb: string
  helper: string
  needsEndpoint: boolean
  /** Prefilled/placeholder endpoint the backend assumes when the field is left blank (fixed cloud service). */
  endpointDefault?: string
}

const PROVIDERS: ProviderInfo[] = [
  {
    id: 'axema',
    name: 'Axema Vaka',
    blurb: 'Single site',
    helper:
      'Vaka panel base URL, e.g. https://192.168.1.10 — one panel is one site, so a successful verification activates the connector directly (no site selection). Use a dedicated Vaka web-service account.',
    needsEndpoint: true,
  },
  {
    id: 'techfass',
    name: 'TECHFASS',
    blurb: 'Multi-site',
    helper:
      'Cloud service (tf-badge.techfass.com) — no endpoint to enter. One login exposes many installations; you pick which ones this customer manages after verification.',
    needsEndpoint: false,
  },
  {
    id: 'acre',
    name: 'Acre / SPC',
    blurb: 'Multi-site',
    helper:
      'Acre Cloud (www.acreintrusion.cloud) is the default — leave the endpoint blank unless you run a local portal. One login exposes many panels — you pick sites after verification. The FlexC receiver (the panel dials us — no endpoint needed) can run alongside.',
    needsEndpoint: true,
    endpointDefault: 'https://www.acreintrusion.cloud',
  },
  {
    id: 'came',
    name: 'CAME BPT',
    blurb: 'Multi-site',
    helper:
      'Cloud service (app.cameconnect.net) — no endpoint to enter. One account exposes many devices across sites; you pick them after verification. CAME syncs by import, not by live polling.',
    needsEndpoint: false,
  },
  {
    id: 'wisenet',
    name: 'Wisenet WAVE',
    blurb: 'Single server',
    helper:
      'WAVE server URL, e.g. https://192.168.1.20:7001 — pick Via appliance under Connectivity if only the edge appliance can reach it, Direct for a public hostname or port-forward. A self-signed certificate can be pinned on the system page after creation. Needs WAVE 5.0 or newer (v3 REST). Use a dedicated WAVE user that can view cameras and manage bookmarks.',
    needsEndpoint: true,
  },
]

const STEP_DEFS = [
  { key: 'credentials', tab: 'Credentials', label: 'Credentials' },
  { key: 'reach', tab: 'Reach', label: 'Connectivity' },
  { key: 'flexc', tab: 'FlexC', label: 'FlexC receiver' },
  { key: 'verify', tab: 'Verify', label: 'Verification' },
  { key: 'sites', tab: 'Sites', label: 'Site selection' },
  { key: 'summary', tab: 'Done', label: 'Summary' },
] as const

type StepKey = (typeof STEP_DEFS)[number]['key']

/** Providers that can sit on a customer LAN the cloud cannot reach directly —
 *  these get the reach step (direct vs via gateway appliance): Axema Vaka
 *  panels and Wisenet WAVE servers (both reached over the edge tunnel when
 *  only the appliance can see them). Every other provider is cloud-direct or
 *  the panel dials us (acre FlexC). */
const LAN_BOUND: ReadonlySet<SystemProvider> = new Set(['axema', 'wisenet'])

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

function CheckLine({ ok, label }: { ok: boolean; label: string }) {
  return (
    <p className={`flex items-center gap-2 text-sm ${ok ? 'text-ostrel-ok' : 'text-ostrel-danger'}`}>
      <span aria-hidden>{ok ? '✓' : '✗'}</span>
      {label}
    </p>
  )
}

export default function SystemOnboardingPage() {
  const navigate = useNavigate()
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: [scope] })

  const [stepIndex, setStepIndex] = useState(0)
  const [provider, setProvider] = useState<SystemProvider>('axema')
  const [name, setName] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [celeryUsername, setCeleryUsername] = useState('')
  const [celeryPassword, setCeleryPassword] = useState('')
  const [wsUsername, setWsUsername] = useState('')
  const [wsPassword, setWsPassword] = useState('')
  const [dryRun, setDryRun] = useState(false)
  const [reach, setReach] = useState<'direct' | 'tunnel'>('direct')
  const [reachGateway, setReachGateway] = useState('')
  const [created, setCreated] = useState<AccessSystem | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  // Acre is multi-integration: web (portal scraper) and/or flexc (panel dials
  // us). Other providers always get an implicit 'web' integration.
  const [integWeb, setIntegWeb] = useState(true)
  const [integFlexc, setIntegFlexc] = useState(false)
  // FlexC step state — collected pre-creation and POSTed with the integration.
  const [flexcPanelId, setFlexcPanelId] = useState('')
  const [flexcReceiverId, setFlexcReceiverId] = useState('')
  const [flexcPollInterval, setFlexcPollInterval] = useState(String(DEFAULT_POLL_INTERVAL))
  const [flexcKey, setFlexcKey] = useState<string | null>(null)
  const [shownKey, setShownKey] = useState<string | null>(null)
  const [keyCopied, setKeyCopied] = useState(false)

  const info = PROVIDERS.find((p) => p.id === provider) ?? PROVIDERS[0]
  const isAcre = provider === 'acre'
  const wantWeb = !isAcre || integWeb
  const wantFlexc = isAcre && integFlexc
  // The reach step only makes sense for a connection WE dial — FlexC-only acre
  // systems have nothing to reach (the panel calls us).
  const needsReach = LAN_BOUND.has(provider) && wantWeb
  const steps = STEP_DEFS.filter((s) => {
    if (s.key === 'reach') return needsReach
    if (s.key === 'flexc') return wantFlexc
    // Nothing to verify and no sites to enumerate without the web side.
    if (s.key === 'verify' || s.key === 'sites') return wantWeb
    if (s.key === 'summary') return !wantWeb
    return true
  })
  const step = Math.min(stepIndex, steps.length - 1)
  const current: StepKey = steps[step].key
  const goTo = (key: StepKey) => {
    const idx = steps.findIndex((s) => s.key === key)
    if (idx >= 0) setStepIndex(idx)
  }

  const gateways = useEdgeGateways()
  const connectedGateways = (gateways.data ?? []).filter((g) => g.connected)

  const sites = useAvailableSites(current === 'sites' ? created?.unique_id : undefined)

  const create = useMutation({
    mutationFn: async () => {
      const system = await api.createSystem({
        provider,
        name: name.trim() || info.name,
        dry_run: dryRun,
        config: wantWeb && baseUrl.trim() ? { base_url: baseUrl.trim() } : {},
        reach: needsReach ? reach : 'direct',
        gateway: needsReach && reach === 'tunnel' ? reachGateway || null : null,
        // FlexC-only acre systems have no web credentials at all (omitted =
        // stored empty). Axema namespaces panel users per role; celery/
        // websocket fall back to main server-side when not provisioned.
        ...(wantWeb
          ? {
              secrets:
                provider === 'axema'
                  ? {
                      main: { username, password },
                      ...(celeryUsername.trim()
                        ? { celery: { username: celeryUsername.trim(), password: celeryPassword } }
                        : {}),
                      ...(wsUsername.trim()
                        ? { websocket: { username: wsUsername.trim(), password: wsPassword } }
                        : {}),
                    }
                  : { username, password },
            }
          : {}),
      })
      // The backend does NOT auto-create integrations for new systems — the
      // wizard declares the chosen set (web with default flags; flexc with the
      // config/key collected on the FlexC step).
      if (wantWeb) await api.createSystemIntegration(system.unique_id, { type: 'web' })
      if (wantFlexc) {
        const config: Record<string, unknown> = {}
        if (flexcReceiverId.trim()) config.flexc_receiver_id = flexcReceiverId.trim()
        if (flexcPanelId.trim()) config.flexc_panel_id = flexcPanelId.trim()
        const poll = Number(flexcPollInterval)
        if (Number.isFinite(poll) && poll >= 1) config.flexc_poll_interval = poll
        await api.createSystemIntegration(system.unique_id, {
          type: 'flexc',
          config,
          ...(flexcKey ? { secrets: { flexc_key: flexcKey } } : {}),
        })
      }
      return system
    },
    onSuccess: (s) => {
      setCreated(s)
      invalidate()
      goTo(wantWeb ? 'verify' : 'summary')
    },
  })

  const verify = useMutation({
    mutationFn: api.verifySystem,
    onSuccess: invalidate,
  })

  // Auto-run verification once when the verify step is entered (StrictMode-safe).
  const verifyRequested = useRef<string | null>(null)
  useEffect(() => {
    if (current === 'verify' && created && verifyRequested.current !== created.unique_id) {
      verifyRequested.current = created.unique_id
      verify.mutate(created.unique_id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, created])

  const saveSites = useMutation({
    mutationFn: (body: { site_key: string; label: string }[]) =>
      api.setManagedSites(created?.unique_id ?? '', body),
    onSuccess: () => {
      invalidate()
      navigate(created ? `/systems/${created.unique_id}` : '/systems')
    },
  })

  const verdict = verify.data ?? null
  const verified = verdict !== null && (verdict.status === 'needs_sites' || verdict.status === 'active')
  const integrationsOk = !isAcre || integWeb || integFlexc
  const webFieldsOk =
    !wantWeb ||
    (username.trim().length > 0 &&
      password.length > 0 &&
      (!info.needsEndpoint || info.endpointDefault !== undefined || baseUrl.trim().length > 0))
  const canSubmitCredentials = integrationsOk && webFieldsOk

  function toggleSite(key: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function saveSelection() {
    const labels = new Map((sites.data ?? []).map((s) => [s.site_key, s.label || s.site_key]))
    saveSites.mutate([...selected].map((key) => ({ site_key: key, label: labels.get(key) ?? key })))
  }

  const skipLink = (
    <Link
      to="/systems"
      className="text-[11px] text-ostrel-ink-muted underline-offset-2 hover:text-ostrel-gold hover:underline"
    >
      Skip for now{created ? ' — the system is saved as draft' : ''}
    </Link>
  )

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section={`Step ${step + 1} · ${steps[step].label}`}
        title={`Add system · ${created?.name ?? info.name}`}
        action={skipLink}
      />

      {/* Step indicator */}
      <div className="flex border-b border-ostrel-line">
        {steps.map((s, idx) => (
          <div
            key={s.key}
            aria-current={step === idx ? 'step' : undefined}
            className={`px-8 py-2.5 text-[11px] font-semibold uppercase tracking-widest ${
              step === idx
                ? 'bg-ostrel-sand text-ostrel-gold shadow-[inset_0_-2px_0_0_var(--color-ostrel-gold)]'
                : 'text-ostrel-ink-muted'
            }`}
          >
            {s.tab}
          </div>
        ))}
      </div>

      {current === 'credentials' && (
        <Card>
          <form
            className="flex flex-col gap-6"
            onSubmit={(e) => {
              e.preventDefault()
              if (!canSubmitCredentials) return
              // LAN-bound providers pick the reach mode before the draft is
              // created (reach/gateway ride along in the create POST).
              if (needsReach) goTo('reach')
              else if (wantFlexc) goTo('flexc')
              else if (!create.isPending) create.mutate()
            }}
          >
            <div>
              <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                Provider
              </p>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {PROVIDERS.map((p) => {
                  const active = p.id === provider
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setProvider(p.id)}
                      aria-pressed={active}
                      className={`rounded-sm border px-4 py-3.5 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-ostrel-gold ${
                        active
                          ? 'border-ostrel-gold bg-ostrel-sand'
                          : 'border-ostrel-line bg-ostrel-card hover:bg-ostrel-sand'
                      }`}
                    >
                      <ProviderTag provider={p.id} />
                      <span className="mt-2 block text-sm font-medium text-ostrel-ink">{p.name}</span>
                      <span className="mt-0.5 block text-[11px] text-ostrel-ink-muted">{p.blurb}</span>
                    </button>
                  )
                })}
              </div>
              <p className="mt-2.5 text-[11px] leading-relaxed text-ostrel-ink-muted">{info.helper}</p>
            </div>

            {isAcre && (
              <div>
                <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                  Integrations
                </p>
                <div className="flex flex-col gap-2.5">
                  <label className="flex cursor-pointer items-start gap-2.5">
                    <input
                      type="checkbox"
                      checked={integWeb}
                      onChange={(e) => setIntegWeb(e.target.checked)}
                      className="mt-0.5 h-3.5 w-3.5 accent-ostrel-gold"
                    />
                    <span>
                      <span className="block text-sm font-medium text-ostrel-ink">Web portal</span>
                      <span className="block text-[11px] leading-snug text-ostrel-ink-muted">
                        Scrapes the panel's web UI — needs base URL + credentials.
                      </span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-2.5">
                    <input
                      type="checkbox"
                      checked={integFlexc}
                      onChange={(e) => setIntegFlexc(e.target.checked)}
                      className="mt-0.5 h-3.5 w-3.5 accent-ostrel-gold"
                    />
                    <span>
                      <span className="block text-sm font-medium text-ostrel-ink">FlexC receiver</span>
                      <span className="block text-[11px] leading-snug text-ostrel-ink-muted">
                        The panel pushes events and accepts commands — no endpoint needed; you
                        configure the panel with values we give you.
                      </span>
                    </span>
                  </label>
                </div>
                {!integrationsOk && (
                  <p className="mt-2 text-[11px] text-ostrel-danger">Pick at least one integration.</p>
                )}
              </div>
            )}

            <Input
              label="Installation name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={info.name}
            />
            {wantWeb && info.needsEndpoint && (
              <div className="flex flex-col gap-1.5">
                <Input
                  label="API endpoint"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder={info.endpointDefault ?? 'https://…'}
                  inputMode="url"
                  required={!info.endpointDefault}
                />
                {info.endpointDefault && !baseUrl.trim() && (
                  <p className="text-xs text-ostrel-ink-muted">
                    Blank uses the default: {info.endpointDefault}
                  </p>
                )}
              </div>
            )}
            {wantWeb && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="API user"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="off"
                  required
                />
                <Input
                  label="Secret"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
            )}

            {provider === 'axema' && (
              <div className="rounded-sm border border-ostrel-line bg-ostrel-sand/40 px-4 py-3">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                  Panel users for background work
                </p>
                <p className="mb-3 text-xs leading-relaxed text-ostrel-ink-muted">
                  Vaka allows only one live session per panel user. The user above runs interactive
                  actions; two more are recommended so background sync and the live event listener
                  never fight over it. Leave empty to share the main account.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Sync user (celery)"
                    value={celeryUsername}
                    onChange={(e) => setCeleryUsername(e.target.value)}
                    autoComplete="off"
                  />
                  <Input
                    label="Sync secret"
                    type="password"
                    value={celeryPassword}
                    onChange={(e) => setCeleryPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  <Input
                    label="Listener user (websocket)"
                    value={wsUsername}
                    onChange={(e) => setWsUsername(e.target.value)}
                    autoComplete="off"
                  />
                  <Input
                    label="Listener secret"
                    type="password"
                    value={wsPassword}
                    onChange={(e) => setWsPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
              </div>
            )}

            {wantWeb && (
              <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ostrel-ink">
                <input
                  type="checkbox"
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                  className="h-3.5 w-3.5 accent-ostrel-gold"
                />
                Dry run — log write actions without applying them to the connector
              </label>
            )}

            {create.error && (
              <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
                {errDetail(create.error)}
              </p>
            )}

            <div className="flex items-center gap-4">
              <Button type="submit" variant="navy" disabled={!canSubmitCredentials || create.isPending}>
                {needsReach
                  ? 'Continue · Connectivity'
                  : wantFlexc
                    ? 'Continue · FlexC receiver'
                    : create.isPending
                      ? 'Saving…'
                      : 'Verify credentials'}
              </Button>
              <p className="text-xs text-ostrel-ink-muted">
                Credentials are stored as a draft until verification succeeds.
              </p>
            </div>
          </form>
        </Card>
      )}

      {current === 'reach' && (
        <Card title="How is this system reached?">
          <div className="flex flex-col gap-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setReach('direct')}
                aria-pressed={reach === 'direct'}
                className={`rounded-sm border px-4 py-3.5 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-ostrel-gold ${
                  reach === 'direct'
                    ? 'border-ostrel-gold bg-ostrel-sand'
                    : 'border-ostrel-line bg-ostrel-card hover:bg-ostrel-sand'
                }`}
              >
                <span className="block text-sm font-medium text-ostrel-ink">Direct</span>
                <span className="mt-0.5 block text-[11px] leading-relaxed text-ostrel-ink-muted">
                  The Ostrel Security cloud reaches the system over the internet (public endpoint or port
                  forward).
                </span>
              </button>
              <button
                type="button"
                onClick={() => setReach('tunnel')}
                aria-pressed={reach === 'tunnel'}
                className={`rounded-sm border px-4 py-3.5 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-ostrel-gold ${
                  reach === 'tunnel'
                    ? 'border-ostrel-gold bg-ostrel-sand'
                    : 'border-ostrel-line bg-ostrel-card hover:bg-ostrel-sand'
                }`}
              >
                <span className="block text-sm font-medium text-ostrel-ink">Via appliance</span>
                <span className="mt-0.5 block text-[11px] leading-relaxed text-ostrel-ink-muted">
                  A gateway appliance on the customer's network relays traffic to the LAN-bound
                  system.
                </span>
              </button>
            </div>

            {reach === 'tunnel' && (
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="reach-gateway"
                  className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted"
                >
                  Gateway appliance
                </label>
                <select
                  id="reach-gateway"
                  value={reachGateway}
                  onChange={(e) => setReachGateway(e.target.value)}
                  className="max-w-md rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
                >
                  <option value="">— pick a connected gateway</option>
                  {connectedGateways.map((g) => (
                    <option key={g.unique_id} value={g.unique_id}>
                      {g.name} ({g.unique_id})
                    </option>
                  ))}
                </select>
                {gateways.isPending ? (
                  <p className="text-[11px] text-ostrel-ink-muted">Loading gateways…</p>
                ) : connectedGateways.length === 0 ? (
                  <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
                    No connected gateways on this customer — provision one under{' '}
                    <Link to="/gateways" className="text-ostrel-gold hover:underline">
                      Gateways
                    </Link>{' '}
                    and enroll the appliance first, or choose Direct.
                  </p>
                ) : null}
              </div>
            )}

            {create.error && (
              <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
                {errDetail(create.error)}
              </p>
            )}

            <div className="flex items-center gap-4">
              <Button
                variant="navy"
                disabled={create.isPending || (reach === 'tunnel' && !reachGateway)}
                onClick={() => (wantFlexc ? goTo('flexc') : create.mutate())}
              >
                {wantFlexc ? 'Continue · FlexC receiver' : create.isPending ? 'Saving…' : 'Verify credentials'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => goTo('credentials')}>
                ← Back
              </Button>
            </div>
          </div>
        </Card>
      )}

      {current === 'flexc' && (
        <Card title="FlexC receiver">
          <div className="flex flex-col gap-5">
            <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
              The panel dials out to our receiver and holds one permanent session — there is no
              endpoint or credentials on our side. Enter these values on the Vanderbilt SPC panel
              under <span className="font-medium text-ostrel-ink">Kommunikationer → FlexC</span>;
              the per-panel values below are stored on the integration when the system is created.
            </p>

            <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
              {PANEL_CONSTANTS.map((row) => (
                <div key={row.label} className="flex items-baseline justify-between gap-3">
                  <dt className="text-xs text-ostrel-ink-muted">{row.label}</dt>
                  <dd className="tnum font-mono text-sm font-medium text-ostrel-ink">{row.value}</dd>
                </div>
              ))}
            </dl>

            <div className="grid gap-4 border-t border-ostrel-line pt-4 sm:grid-cols-3">
              <Input
                label="Enhets-ID"
                value={flexcPanelId}
                onChange={(e) => setFlexcPanelId(e.target.value)}
                autoComplete="off"
              />
              <Input
                label="Mottagar-ID"
                value={flexcReceiverId}
                onChange={(e) => setFlexcReceiverId(e.target.value)}
                autoComplete="off"
              />
              <Input
                label="Poll interval (s)"
                type="number"
                min={1}
                value={flexcPollInterval}
                onChange={(e) => setFlexcPollInterval(e.target.value)}
              />
            </div>
            <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
              Enhets-ID is globally unique across all panels and operator-assigned — pick an unused
              number. We are the poll master; the interval defaults to {DEFAULT_POLL_INTERVAL} s.
            </p>

            <div className="flex flex-wrap items-center gap-3 border-t border-ostrel-line pt-4">
              <span className="text-xs text-ostrel-ink-muted">Kryptering</span>
              <Button
                variant="navy"
                size="sm"
                onClick={() => {
                  const key = generateFlexcKey()
                  setFlexcKey(key)
                  setKeyCopied(false)
                  setShownKey(key)
                }}
              >
                Generate key
              </Button>
              <span className="text-[11px] leading-relaxed text-ostrel-ink-muted">
                {flexcKey
                  ? 'Key generated — it is stored write-only with the system and never shown again.'
                  : 'Generates a 64-character hex key (AES-256), shown once and stored write-only when the system is created.'}
              </span>
            </div>

            {create.error && (
              <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
                {errDetail(create.error)}
              </p>
            )}

            <div className="flex items-center gap-4">
              <Button variant="navy" disabled={create.isPending} onClick={() => create.mutate()}>
                {create.isPending ? 'Saving…' : 'Create system'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => goTo(needsReach ? 'reach' : 'credentials')}>
                ← Back
              </Button>
            </div>
          </div>

          <Modal open={shownKey !== null} title="FlexC encryption key" onClose={() => setShownKey(null)}>
            <p className="text-xs leading-relaxed text-ostrel-ink-muted">
              Enter this key on the panel now. It is stored write-only and{' '}
              <strong className="text-ostrel-ink">cannot be retrieved later</strong> — this is the
              only time it is shown.
            </p>
            <code
              data-testid="flexc-key"
              className="tnum mt-3 block break-all rounded-sm border border-ostrel-line bg-ostrel-sand px-3 py-2.5 font-mono text-sm text-ostrel-ink"
            >
              {shownKey}
            </code>
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (shownKey) void navigator.clipboard.writeText(shownKey).then(() => setKeyCopied(true))
                }}
              >
                {keyCopied ? 'Copied' : 'Copy'}
              </Button>
              <Button variant="navy" size="sm" onClick={() => setShownKey(null)}>
                Done
              </Button>
            </div>
          </Modal>
        </Card>
      )}

      {current === 'verify' && created && (
        <Card title="Verification">
          {wantFlexc && (
            <p className="mb-4 text-[11px] leading-relaxed text-ostrel-ink-muted">
              Verification covers the web portal side. The FlexC receiver needs none — it activates
              when the panel connects, and stays configurable on the system detail page.
            </p>
          )}
          {verify.isPending ? (
            <div className="flex items-center gap-3 py-2 text-sm text-ostrel-ink-muted">
              <Spinner /> Contacting {info.name}…
            </div>
          ) : verify.isError ? (
            <div className="flex flex-col gap-3">
              <CheckLine ok={false} label="Handshake" />
              <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
                {errDetail(verify.error)}
              </p>
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => verify.mutate(created.unique_id)}
                >
                  Retry
                </Button>
              </div>
            </div>
          ) : verdict ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <CheckLine ok label="Handshake" />
                <CheckLine ok={verdict.status !== 'auth_failed'} label="Service account" />
                <CheckLine ok={verified} label="Enumerate installations" />
              </div>
              {verified ? (
                <p className="rounded-sm border border-ostrel-ok/40 bg-ostrel-ok/10 px-3 py-2 text-xs text-ostrel-ok">
                  {verdict.status === 'active'
                    ? 'Authenticated — installation registered, the connector is active.'
                    : (verdict.status_detail || 'Authenticated — select which installations to manage.')}
                </p>
              ) : (
                <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
                  {verdict.status_detail || 'Verification failed — check the credentials and try again.'}
                </p>
              )}
              <div className="flex items-center gap-3">
                {verdict.status === 'needs_sites' && (
                  <Button variant="primary" onClick={() => goTo('sites')}>
                    Continue · Select sites
                  </Button>
                )}
                {verdict.status === 'active' && (
                  <Button variant="primary" onClick={() => navigate(`/systems/${created.unique_id}`)}>
                    Finish
                  </Button>
                )}
                {!verified && (
                  <Button
                    variant="ghost"
                    disabled={verify.isPending}
                    onClick={() => verify.mutate(created.unique_id)}
                  >
                    Retry
                  </Button>
                )}
                {skipLink}
              </div>
            </div>
          ) : null}
        </Card>
      )}

      {current === 'sites' && created && (
        <Card
          title="Select installations to manage"
          action={
            sites.data && sites.data.length > 0 ? (
              <span className="tnum text-[11px] text-ostrel-ink-muted">
                {selected.size} selected
              </span>
            ) : undefined
          }
        >
          {sites.isPending ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
            </div>
          ) : sites.isError ? (
            <EmptyState
              title="Could not list installations"
              hint={errDetail(sites.error)}
              action={
                <Button variant="ghost" size="sm" onClick={() => sites.refetch()}>
                  Retry
                </Button>
              }
            />
          ) : sites.data.length === 0 ? (
            <EmptyState
              title="No installations on this account"
              hint="The verified login reports no sites. Save to finish anyway, or skip and revisit later."
            />
          ) : (
            <ul className="flex flex-col gap-2.5">
              {sites.data.map((site) => {
                const checked = selected.has(site.site_key)
                return (
                  <li key={site.site_key}>
                    <label
                      className={`flex cursor-pointer items-center gap-3 rounded-sm border px-3.5 py-3 transition-colors ${
                        checked
                          ? 'border-ostrel-gold bg-ostrel-sand'
                          : 'border-ostrel-line bg-ostrel-card hover:bg-ostrel-sand'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSite(site.site_key)}
                        className="h-3.5 w-3.5 accent-ostrel-gold"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-ostrel-ink">
                          {site.label || site.site_key}
                        </span>
                        <span className="tnum block font-mono text-[11px] text-ostrel-ink-muted">
                          {site.site_key}
                        </span>
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          )}

          {saveSites.error && (
            <p className="mt-3 rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
              {errDetail(saveSites.error)}
            </p>
          )}

          <div className="mt-5 flex items-center gap-4">
            <Button
              disabled={saveSites.isPending || selected.size === 0}
              onClick={saveSelection}
            >
              {saveSites.isPending ? 'Activating…' : 'Activate connector'}
            </Button>
            <p className="text-xs text-ostrel-ink-muted">
              Selecting at least one installation flips the connector to active.
            </p>
            {skipLink}
          </div>
        </Card>
      )}

      {current === 'summary' && created && (
        <Card title="Ready for the panel">
          <div className="flex flex-col gap-4">
            <p className="text-sm leading-relaxed text-ostrel-ink">
              {created.name} is saved with its FlexC receiver integration. There is nothing to verify
              from our side — the integration comes online when the panel dials{' '}
              <span className="tnum font-mono text-[13px]">
                {FLEXC_RECEIVER_HOST}:{FLEXC_RECEIVER_PORT}
              </span>
              .
            </p>
            <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
              The panel-side values, the encryption key, and the integration's capability flags live
              on the system detail page.
            </p>
            <div className="flex items-center gap-4">
              <Button variant="primary" onClick={() => navigate(`/systems/${created.unique_id}`)}>
                Open system
              </Button>
              {skipLink}
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}
